import { Injectable, Inject } from '@angular/core';
import { Observable, BehaviorSubject, Subject, interval, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { AbstractLoginService } from '../../declaration/abstract-member-service';
import { MEMBER_LOGIN_WAY } from '../../entity/member-login-way';
import { LoginResultCode } from '../../declaration/LoginResultCode.enum';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from 'src/app/web-generator/generator-env-token';
import { GlobalComponentService } from '@techmore/global';
import { CommonHttpClientService } from 'src/app/share/service/common-http-client.service';

declare let google: any;
/**
 * Google 登入服務
 *
 * 登入流程:
 * 開啟google登入對話視窗 --> 登入後轉導回網站 memberLoading? 頁面，取得 authCode --> 用 authCode 向 seerver 取得會員資料
 */
@Injectable()
export class GoogleService extends AbstractLoginService {
  protected _isRefreshing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  auth2: any;
  /** google 登入後取得憑證權杖，通知其他人(看有誰就誰吧)處理接下來的平台登入流程 */
  handleCredentialResponse$ = new Subject<any>();
  /** GSI 登入後要轉導到 server 的網址 */
  protected gsiRedirectUrl: string;;
  /** google client id 設定通知器 */
  private clientId$ = new BehaviorSubject(null);
  /** google client id 設定通知器 */
  getClientId$() { return this.clientId$.asObservable(); }
  /** google client id */
  get clientId() { return this.clientId$.value; }

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) protected generatorEnv: GeneratorEnv,
    protected http: CommonHttpClientService,
    protected globalComponentService: GlobalComponentService,
  ) {
    super(generatorEnv);
  }

  init(clientId: string) {
    if (clientId) {
      if (clientId !== this.clientId$.value) {
        this.clientId$.next(clientId);
      }

      const ua = window.location.href.split('/');
      if (this.generatorEnv.mode === 'path') {
        this.gsiRedirectUrl = `${this.generatorEnv.apiHost}/gsi/login?origin=` + btoa(JSON.stringify({ domain: ua[2], path: ua[3], lang: ua[4] }));
      } else {
        this.gsiRedirectUrl = `${this.generatorEnv.apiHost}/gsi/login?origin=` + btoa(JSON.stringify({ domain: ua[2], lang: ua[3] }));
      }


      if (!document.getElementById('google_api')) {
        (window as any).handleCredentialResponse = (response) => {
          console.log("GSI Encoded JWT ID token: " + response.credential);
          this.handleCredentialResponse$.next(response.credential);
        };

        const script = document.createElement('script');
        script.id = 'google_api';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        script.onload = () => {
          google.accounts.id.initialize({
            client_id: this.clientId,
            context: 'signin',
            // callback: (window as any).handleCredentialResponse,
            login_uri: this.gsiRedirectUrl,
            ux_mode: 'redirect',
          });

          this.loadMemberInfoFromStorage();
          this.initFinish$.next(true);

          // google.accounts.id.prompt(); // also display the One Tap dialog
        };
        script.src = 'https://accounts.google.com/gsi/client';
      }
    } else {
      localStorage.removeItem('auth_code');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_refresh_token');
      localStorage.removeItem('auth_way');
    }
  }

  /**
   * 渲染 google 登入按鈕
   * @param btnDiv 要塞入按鈕的區塊
   */
  rendLoginBtn(btnDiv: HTMLElement, width: number): void {
    const generatorEnv: any = {
      type: 'standard',
      shape: 'rectangular',
      theme: "outline",
      text: 'signin_with',
      size: "large",
      locale: 'zh-TW',
      logo_alignment: 'left',
    };
    if (width) { generatorEnv.width = width; }

    google.accounts.id.renderButton(
      btnDiv, generatorEnv // customization attributes
    );
  }

  /** 將 sessionStorage 裡已有的auth_token載入到服務裡 */
  loadMemberInfoFromStorage() {
    let data: any;
    data = localStorage.getItem('auth_token');
    if (localStorage.getItem('auth_way') === MEMBER_LOGIN_WAY.GOOGLE && data) {
      try {
        const memberInfo = this.parseToken(data);
        if (JSON.stringify(memberInfo) !== JSON.stringify(this.memberInfo$.value)) {
          this.memberInfo$.next(memberInfo);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  login(): Observable<any> {
    this.onLoginStart$.next();
    const openedWindow = window.open(
      `https://accounts.google.com/gsi/select?client_id=${this.clientId}&ux_mode=redirect&login_uri=${this.gsiRedirectUrl}&ui_mode=card&context=signin&as=Ph2KzYAlQ9OoxhaEjXayyg&g_csrf_token=d742c1351acb0dd5`,
      '_blank',
      'height=700,resizable=yes,scrollbars=yes,width=700'
    );

    return new Observable(subscriber => {
      const checkWinClosedSubc = interval(1000).subscribe(() => {
        if (openedWindow.closed) {
          if (window['loginErrorReason']) {
            const error = window['loginErrorReason'];
            delete window['loginErrorReason'];
            this.onLoginEnd$.next(LoginResultCode.fail);
            checkWinClosedSubc.unsubscribe();
            subscriber.error(new Error(JSON.stringify(error)));
            subscriber.complete();
          } else {
            if (localStorage.getItem('auth_token')) {
              this.loadMemberInfoFromStorage();
              this.onLoginEnd$.next(LoginResultCode.success);
              subscriber.next(true);
            } else {
              this.onLoginEnd$.next(LoginResultCode.cancel);
              subscriber.next(false);
            }
            checkWinClosedSubc.unsubscribe();
            subscriber.complete();
          }
        }
      });
    });

    // return from(this.auth2.grantOfflineAccess()).pipe(
    //   map((authResult: {code: string}) => authResult.code),
    //   switchMap(authCode => {
    //     if (authCode) {
    //       return this.getMembertoken(authCode).pipe(
    //         tap(data => {
    //           try {
    //             const memberInfo = this.parseToken(data.accessToken);
    //             if (JSON.stringify(memberInfo) !== JSON.stringify(this.memberInfo$.value)) {
    //               this.memberInfo$.next(memberInfo);
    //             }
    //           } catch (error) {
    //             console.error(error);
    //           }
    //           this.onLoginEnd$.next(LoginResultCode.success);
    //         }),
    //       )
    //     }
    //     return Observable.throw(new Error('gapi dosen\'t get authCode'));
    //   }),
    //   catchError((err, caught) => {
    //     if (err.error === 'popup_closed_by_user') {
    //       this.onLoginEnd$.next(LoginResultCode.cancel);
    //     }
    //     if (!(err instanceof HttpErrorResponse)) {
    //       console.error(err);
    //     }
    //     this.onLoginEnd$.next(LoginResultCode.fail);
    //     return Observable.throw(err);
    //   })
    // );
  }

  logout(): Observable<any> {
    google.accounts.id.disableAutoSelect();
    return new Observable<any>((subscriber) => {
      localStorage.removeItem('auth_code');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_refresh_token');
      localStorage.removeItem('auth_way');
      this.logout$.next();
      this.memberInfo$.next(undefined);
      subscriber.next(true);
    });
  }

  getMembertoken(authCode: string): Observable<any> {
    const url = `${this.generatorEnv.apiHost}/api/member/oauth/token?grant_type=google&code=${authCode}`;
    return this.http.post<any>(url, JSON.stringify({}), {
      headers: {
        'access_token': localStorage.getItem('site_token')
      }
    }).pipe(
      catchError((error, observable) => this.errorHandler(error, observable)),
      tap(data => {
        localStorage.setItem('auth_token', data['accessToken']);
        localStorage.setItem('auth_refresh_token', data['refreshToken']);
        localStorage.setItem('auth_way', MEMBER_LOGIN_WAY.GOOGLE);
        const memberInfo = this.parseToken(data['accessToken']);
        this.memberInfo$.next(memberInfo);
      })
    );
  }

  parseToken(authToken: string) {
    const base64Url = authToken.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    switch (base64.length % 4) {
      case 0: break;
      case 2: base64 += '=='; break;
      case 3: base64 += '='; break;
      default: break;
    }
    return JSON.parse(decodeURIComponent(escape(atob(base64))));
  }

  refreshMemberToken = (refreshToken: any): Observable<any> => {
    const url = `${this.generatorEnv.apiHost}/member/oauth/token?grant_type=auth_refresh_token&auth_refresh_token=${refreshToken}`;
    return this.http.post<any>(url, JSON.stringify({}));
  };

  errorHandler = (error: HttpErrorResponse, observable) => {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 611:
          let subject = new Subject<any>();

          if (!this._isRefreshing.getValue()) {
            this._isRefreshing.next(true);
            this.refreshMemberToken(localStorage.getItem('auth_refresh_token')).subscribe(data => {
              localStorage.setItem('auth_token', data['accessToken']);
              localStorage.setItem('auth_refresh_token', data['refreshToken']);
              this._isRefreshing.next(false);
            }, error1 => {
              this.globalComponentService.openSanckBar('會員已被登出，請重新登入', 'X', {duration: 5000, horizontalPosition: 'right'});
              localStorage.removeItem('auth_code');
              localStorage.removeItem('auth_token');
              localStorage.removeItem('auth_refresh_token');
              localStorage.removeItem('auth_way');
              this.logout$.next();
              this.memberInfo$.next(undefined);
            });
          }

          this._isRefreshing.pipe(
            filter(bool => !bool)
          ).subscribe((bool) => observable.subscribe(subject));

          return subject;
        default:
          break;
      }
    } else {
      // 未預期的程式錯誤
      console.error(error);
    }

    return throwError(error);
  }
}
